import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { UploadSimple, PaperPlaneTilt, ArrowClockwise, X } from '@phosphor-icons/react';
import AnalysisReport from '../components/AnalysisReport';
import DeepReasoningToggle from '@/components/DeepReasoningToggle';
import DictateButton from '@/components/DictateButton';
import { getRole } from '@/components/RoleGate';
import { announce } from '@/lib/alerts';
import { api, analyzeText, analyzeImage, fileToBase64 } from '@/lib/api';
import { ANALYZE } from '@/constants/testIds/analysis';
import { errMsg } from '@/lib/api';

const SAMPLES = [
  { title: 'Romance / love-bombing', text: `THEM: I know we just matched but I feel like I've known you forever, you're literally my soulmate. Delete Tinder, I only want you.\nME: haha slow down\nTHEM: sorry I get intense when I care. Also small favor — my card is frozen abroad, can you send $200 for a hotel? I'll pay back x3` },
  { title: 'Crypto pitch friend', text: `THEM: bro I made $40k in 2 weeks on this coin, don't miss out, private group closes tonight, DM my broker Aleks\nME: what is it exactly\nTHEM: don't overthink, opportunity of a lifetime, everyone in is up. urgent.` },
  { title: 'Neutral chat', text: `ME: are we still on for coffee saturday?\nTHEM: yes 10am at the usual place. bring the book.\nME: got it` },
];

export default function Dashboard() {
  const [conversation, setConversation] = useState('');
  const [context, setContext] = useState('');
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!conversation.trim() && !image) {
      toast.error('Paste a conversation or drop a screenshot first');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = image
        ? await analyzeImage({ image_base64: image.b64, mime_type: image.mime, context })
        : await analyzeText({ conversation, context });
      setResult(r);
      announce({ score: r.risk_score, label: r.risk_label, summary: r.one_line_summary || r.one_liner });
      if (getRole() === 'child') api.post('/family/link/alert', { risk: Math.round(r.risk_score || 0), label: r.risk_label || '', source: 'analyze' }).catch(() => {});
      toast.success(`Risk read: ${r.risk_label} · ${r.risk_score}`);
    } catch (e) {
      toast.error(errMsg(e, 'Analysis failed'));
    } finally {
      setLoading(false);
    }
  };

  const onFile = async (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('JPG, PNG, or WEBP only');
      return;
    }
    const dataUri = await fileToBase64(file);
    setImage({ b64: dataUri, mime: file.type, preview: dataUri, name: file.name });
  };

  const clear = () => {
    setConversation(''); setContext(''); setImage(null); setResult(null);
  };

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr),380px]">
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="relative flex h-2 w-2">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-70" />
              <span className="relative rounded-full bg-emerald-400 h-2 w-2" />
            </span>
            <span className="label-mini text-emerald-400">guardian · listening in real time</span>
          </div>
          <h1 className="font-sans text-4xl md:text-5xl font-black tracking-tighter text-zinc-50 leading-[0.95]">
            Someone has your back.<br />
            <span className="text-zinc-500">Now. In real time.</span>
          </h1>
          <p className="mt-4 text-sm text-zinc-400 max-w-xl leading-relaxed">
            Paste a chat, drop a screenshot, or open Live and speak. Your second conscience reads
            the dynamics as they happen — the manipulation tactics, the potential harms, the
            potential benefits — and tells you exactly what to do next. Warm when you're OK. Loud
            when you're not.
          </p>
        </div>

        <div className="border border-amber-500/50 bg-amber-500/5 p-4 md:p-5 space-y-3" data-testid="owner-note">
          <p className="label-mini text-amber-400">a note from the owner</p>
          <p className="text-sm text-zinc-200 leading-relaxed">
            I can't afford to make this site as fast and efficient as I would like, so please be patient. It is a little
            slow, but it's going to give you a lot of information.
          </p>
          <p className="text-sm text-zinc-200 leading-relaxed">
            I own it. I built it. It's 100% mine, no investors. <b className="text-zinc-50">If you're interested in helping me
            further this project</b>, please email{' '}
            <a href="mailto:Hoodsidecares2026@gmail.com" data-testid="owner-contact-email" className="text-amber-400 underline break-all">Hoodsidecares2026@gmail.com</a>.
          </p>
          <p className="text-sm text-zinc-200 leading-relaxed">
            Hope you enjoy. Donate if you can — you don't have to donate much. It only costs <b className="text-zinc-50">$100 a month</b> to
            maintain as it is.
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            <a href="https://cash.app/$jameswilloughby1987" target="_blank" rel="noopener noreferrer" data-testid="owner-donate-btn"
              className="inline-flex items-center gap-2 bg-amber-400 px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-950 hover:bg-amber-300">
              donate · cash app
            </a>
            <a href="mailto:Hoodsidecares2026@gmail.com?subject=Helping%20further%20unmanipulated" data-testid="owner-help-btn"
              className="inline-flex items-center gap-2 border border-amber-500/50 px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] text-amber-400 hover:border-amber-400">
              help further this project
            </a>
          </div>
        </div>

        <div className="panel p-5">
          <label className="label-mini block mb-2">Optional context</label>
          <input
            data-testid={ANALYZE.contextInput}
            value={context}
            onChange={e => setContext(e.target.value)}
            placeholder="e.g. Tinder match, 3 days · Instagram friend from 2019 · Ex-partner"
            className="w-full bg-transparent border border-zinc-800 px-3 py-2 text-sm outline-none focus:border-emerald-500/50 transition-colors"
          />

          <label className="label-mini block mt-5 mb-2">Conversation</label>
          <textarea
            data-testid={ANALYZE.conversationInput}
            value={conversation}
            onChange={e => setConversation(e.target.value)}
            placeholder={"ME: hey how's it going\nTHEM: ..."}
            rows={9}
            className="w-full bg-zinc-950 border border-zinc-800 px-3 py-3 text-sm outline-none focus:border-emerald-500/50 resize-none leading-relaxed font-mono"
          />

          <div className="mt-4 flex flex-wrap gap-2">
            {SAMPLES.map(s => (
              <button
                key={s.title}
                onClick={() => { setConversation(s.text); setImage(null); }}
                className="border border-zinc-800 px-3 py-1.5 text-[11px] uppercase tracking-[0.14em] text-zinc-400 hover:border-emerald-500/40 hover:text-emerald-400 transition-colors"
              >
                try · {s.title}
              </button>
            ))}
          </div>
        </div>

        <div className="panel p-5">
          <label className="label-mini block mb-3">or drop a screenshot</label>
          <label
            data-testid={ANALYZE.uploadDrop}
            className="flex items-center justify-between border border-dashed border-zinc-700 hover:border-emerald-500/50 px-4 py-4 cursor-pointer transition-colors"
          >
            <div className="flex items-center gap-3">
              <UploadSimple weight="bold" className="h-5 w-5 text-zinc-500" />
              <span className="text-xs text-zinc-400">
                {image ? image.name : 'Click or drop · JPG / PNG / WEBP'}
              </span>
            </div>
            <input
              data-testid={ANALYZE.uploadInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
          </label>
          {image && (
            <div className="mt-3 flex items-start gap-3">
              <img src={image.preview} alt="preview" className="h-24 w-24 object-cover border border-zinc-800" />
              <button onClick={() => setImage(null)} className="label-mini text-zinc-500 hover:text-red-400 flex items-center gap-1">
                <X className="h-3 w-3" /> remove
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid={ANALYZE.runButton}
            onClick={run}
            disabled={loading}
            className="group inline-flex items-center gap-2 border border-emerald-500 bg-emerald-500 px-6 py-3 text-xs uppercase tracking-[0.24em] font-bold text-zinc-950 hover:-translate-y-0.5 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition-transform"
          >
            {loading ? <ArrowClockwise className="h-4 w-4 animate-spin" /> : <PaperPlaneTilt weight="fill" className="h-4 w-4" />}
            {loading ? 'analyzing…' : 'run analysis'}
          </button>
          <button
            data-testid={ANALYZE.clearButton}
            onClick={clear}
            className="border border-zinc-800 px-4 py-3 text-xs uppercase tracking-[0.24em] text-zinc-400 hover:border-zinc-600 hover:text-zinc-100 transition-colors"
          >
            clear
          </button>
          <DeepReasoningToggle />
          <DictateButton onText={(t) => setConversation(prev => (prev ? prev + '\n' : '') + t)} testid="dashboard-dictate" />
        </div>

        {result && <AnalysisReport data={result} />}
      </div>

      <aside className="space-y-4 lg:sticky lg:top-24 h-fit">
        <div className="panel p-5">
          <div className="label-mini mb-2">Common tactics</div>
          <ul className="text-xs text-zinc-400 space-y-1.5 leading-relaxed">
            <li>— Love-bombing / rapid intimacy</li>
            <li>— Urgency & scarcity pressure</li>
            <li>— Moving off-platform</li>
            <li>— Gaslighting & DARVO</li>
            <li>— Financial ask / "opportunity"</li>
            <li>— Isolation from friends/family</li>
            <li>— Identity vagueness / catfishing</li>
          </ul>
          <Link to="/tactics" data-testid="dashboard-tactics-link" className="mt-3 inline-block text-[10px] uppercase tracking-[0.22em] text-emerald-400 hover:text-emerald-300">full dictionary · 95+ tactics →</Link>
        </div>
        <div className="panel p-5">
          <div className="label-mini mb-2 flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            The Promise
          </div>
          <p className="text-xs text-zinc-300 leading-relaxed">
            <span className="text-zinc-100 font-bold">Never sold. Never surrendered.</span> Your
            words, screenshots, and readings are analyzed and then destroyed. We will never sell
            your information. We will never hand it over to any agency or third party. Not for
            money. Not for pressure. Not for a subpoena we can dodge.
          </p>
          <p className="mt-3 text-xs text-zinc-400 leading-relaxed">
            Baselines and your trusted circle live on your device only — not on our servers.
            That's the whole design. It's why the tool can be honest with you.
          </p>
        </div>
      </aside>
    </div>
  );
}
